import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, FileEntry } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { ApproveModal } from "capitalrx-components-2.0/lib/general/Modal/ApproveModal";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";

import {
  DirectMemberReimbursementFormFillType,
  DirectMemberReimbursementSubmissionRequest,
  DirectMemberReimbursementSubmissionType,
  DMR_FORM_FILL_TYPES,
  DMR_SUBMISSION_TYPES,
  uploadDMRDocumentFileToS3,
  useCreateDMRSubmissionMutation,
  useProcessDMRSubmissionMutation,
} from "@/App/api/directMemberReimbursement";
import { useListMemberDependentsQuery } from "@/App/api/member";
import { ContentPanel, PageWrapper, ThemedDropdown } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context";
import { useIsUnifiedUser, useNavigateClient } from "@/App/hooks";
import { DirectMemberReimbursementClaimDetailsForm } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementClaimDetailsForm";
import { DirectMemberReimbursementPatientDetailsCard } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementPatientDetailsCard";
import { DirectMemberReimbursementUploadClaimForm, UploadClaimForm } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementUploadClaimForm";
import { MedicalDmrInfoSection } from "@/App/pages/DirectMemberReimbursement/MedicalDmrInfoSection";
import { SubmissionLoadingModal } from "@/App/pages/DirectMemberReimbursement/SubmissionLoadingModal";

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementPage";

export const DirectMemberReimbursementPage: FC = () => {
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedClaimType, setSelectedClaimType] = useState<DirectMemberReimbursementSubmissionType>(null);
  const [selectedPatient, setSelectedPatient] = useState<string>(memberEligibility.member.person_code);
  const [selectedFormType, setSelectedFormType] = useState<DirectMemberReimbursementFormFillType>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [submittingDMRPayload, setSubmittingDMRPayload] = useState<DirectMemberReimbursementSubmissionRequest>(null);
  const [documentsToUpload, setDocumentsToUpload] = useState<FileEntry[]>([]);
  const isEmulating = !!sessionStorage.getItem("isEmulation");
  const { isUnifiedUser } = useIsUnifiedUser();
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const showAlert = useAlert();

  if (isEmulating) {
    navigate("/");
  }

  const { data: dependents } = useListMemberDependentsQuery();

  const { mutateAsync: createDMRSubmission } = useCreateDMRSubmissionMutation();
  const { mutateAsync: processDMRSubmission } = useProcessDMRSubmissionMutation();

  const isMedicalUploadSubmission = selectedClaimType === "medical";
  const isPharmacyUploadSubmission = selectedClaimType === "pharmacy" && selectedFormType === "pdf";
  const isPharmacyFormFillSubmission = selectedClaimType === "pharmacy" && selectedFormType === "web";

  useEffect(() => {
    // Set default claim type for pharmacy-only users
    if (!isUnifiedUser && selectedClaimType === null) {
      setSelectedClaimType(DMR_SUBMISSION_TYPES.PHARMACY);
    }
  }, [isUnifiedUser]);

  const { smallCols, medCols, largeCols } = useResponsive({
    smallCols: {
      mobile: 12,
      tablet: 3,
      desktop: 2,
    },
    medCols: {
      mobile: 12,
      tablet: 6,
      desktop: 3,
    },
    largeCols: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);

  const patientToDisplay = useMemo(() => {
    if (selectedPatient === memberEligibility?.member?.person_code) {
      return memberEligibility?.member;
    } else {
      return dependents?.results.find((dependent) => dependent.member.person_code === selectedPatient)?.member;
    }
  }, [dependents, selectedPatient]);

  const getPayloadGenericValues = (): Partial<DirectMemberReimbursementSubmissionRequest> => ({
    client_id: memberEligibility.plan.client_id,
    external_member_id: memberEligibility.member.external_member_id,
    person_code: selectedPatient,
    form_fill_type: selectedFormType,
    submission_type: selectedClaimType,
  });

  const getPayloadForMedicalDocumentFormSubmit = (
    values: UploadClaimForm,
    payload: Partial<DirectMemberReimbursementSubmissionRequest>,
  ) => {
    payload.form_data = {
      medical: {
        claim_form: {
          description: "Medical DMR Submitted Document",
          file_name: values.claim_form[0].name,
          name: "Medical DMR Submitted Document",
        },
      },
    };
    payload.form_fill_type = "pdf";

    setDocumentsToUpload([...documentsToUpload, ...values.claim_form]);

    return payload;
  };

  const getPayloadForPharmacyDocumentFormSubmit = (
    values: UploadClaimForm,
    payload: Partial<DirectMemberReimbursementSubmissionRequest>,
  ) => {
    payload.form_data = {
      pharmacy: {
        claim_form: {
          file_name: values.claim_form[0].name,
          description: "Pharmacy DMR Submitted Document",
          name: "Pharmacy DMR Submitted Document",
        },
        claim_evidence_documents: values.claim_evidence_files.map((dmrDocument) => ({
          file_name: dmrDocument.name,
          description: "Pharmacy DMR Claim Evidence File",
          name: "Pharmacy DMR Claim Evidence File",
        })),
      },
    };

    setDocumentsToUpload([...documentsToUpload, ...values.claim_form, ...values.claim_evidence_files]);

    return payload;
  };

  const onUploadClaimFormSubmit = async (values: UploadClaimForm) => {
    let payload = getPayloadGenericValues();
    payload.contact_email_address = values.email_address;
    payload.contact_phone_number = values.phone_number;

    if (isMedicalUploadSubmission) {
      payload = getPayloadForMedicalDocumentFormSubmit(values, payload);
    } else if (isPharmacyUploadSubmission) {
      payload = getPayloadForPharmacyDocumentFormSubmit(values, payload);
    }

    setSubmittingDMRPayload(payload as DirectMemberReimbursementSubmissionRequest);
    setShowConfirmationModal(true);
  };

  const onFillPharmacyDetailsFormSubmit = (result: {
    payload: Partial<DirectMemberReimbursementSubmissionRequest>;
    documents: FileEntry[];
  }) => {
    const payload = { ...result.payload, ...getPayloadGenericValues() };
    setSubmittingDMRPayload(payload as DirectMemberReimbursementSubmissionRequest);
    setDocumentsToUpload([...documentsToUpload, ...result.documents]);
    setShowConfirmationModal(true);
  };

  const onDMRSubmitConfirmedHandler = async (approved: boolean) => {
    setShowConfirmationModal(false);

    if (!approved) {
      setSubmittingDMRPayload(null);
      setDocumentsToUpload([]);
    } else {
      try {
        setIsSubmitting(true);
        const createDMRResponse = await createDMRSubmission(submittingDMRPayload as DirectMemberReimbursementSubmissionRequest);
        const uploadTasks = [];
        let uploadQueue = [...documentsToUpload];

        for (const documentResponse of createDMRResponse.dmr_documents) {
          const item = uploadQueue.find((dmrDocument) => `${documentResponse.id}-${dmrDocument.name}` === documentResponse.file_name);
          if (!item) {
            throw new Error;
          }
          uploadQueue = uploadQueue.filter((x) => x != item);
          uploadTasks.push(uploadDMRDocumentFileToS3(documentResponse.post, item.file));
        }

        if (uploadQueue.length > 0) {
          throw new Error;
        }

        await Promise.all(uploadTasks);

        await processDMRSubmission(createDMRResponse.dmr_submission?.id);

        setSelectedClaimType(null);
        setSelectedFormType(null);
        setSubmittingDMRPayload(null);
        setDocumentsToUpload([]);
        setIsSubmitting(false);
        navigate(`/direct-member-reimbursement/success/${createDMRResponse.dmr_submission?.confirmation_number}`);
      } catch (error) {
        showAlert({
          message: translate(`${TRANSLATION_PATH}.errorUploadingDMR`),
          type: "error",
          style: "toast",
        });
        setIsSubmitting(false);
      }
    }
  };

  return (
    <PageWrapper>
      <SubmissionLoadingModal isVisible={isSubmitting} />
      <ContentPanel showBorder={false} title={translate(`${TRANSLATION_PATH}.submitAReimbursementClaim`)}>
        <ApproveModal
          title={translate(`${TRANSLATION_PATH}.confirmSubmission`)}
          visible={showConfirmationModal}
          approveContent={translate(`${TRANSLATION_PATH}.submit`)}
          denyContent={translate(`${TRANSLATION_PATH}.cancel`)}
          onClose={onDMRSubmitConfirmedHandler}
          size={"narrow"}
        >
          <Body>{translate(`${TRANSLATION_PATH}.confirmSubmissionDescription`)}</Body>
        </ApproveModal>
        <Stack spacing="large">
          <Stack spacing="medium">
            <Body>{translate(`${TRANSLATION_PATH}.claimTypeDropdown`)}</Body>
            <Grid>
              <Grid.Span columns={smallCols}>
                <Dropdown
                  placeholder={translate(`${TRANSLATION_PATH}.select`)}
                  onChange={(claimType: DirectMemberReimbursementSubmissionType) => {
                    setSelectedFormType(null);
                    setSelectedClaimType(claimType);
                  }}
                  testId="claim-type-dropdown"
                  value={selectedClaimType}
                  hideLabel
                >
                  <Dropdown.Option value={DMR_SUBMISSION_TYPES.PHARMACY}>
                    {translate(`${TRANSLATION_PATH}.${DMR_SUBMISSION_TYPES.PHARMACY}`)}
                  </Dropdown.Option>
                  {isUnifiedUser && (
                    <Dropdown.Option value={DMR_SUBMISSION_TYPES.MEDICAL}>
                      {translate(`${TRANSLATION_PATH}.${DMR_SUBMISSION_TYPES.MEDICAL}`)}
                    </Dropdown.Option>
                  )}
                </Dropdown>
              </Grid.Span>
            </Grid>
          </Stack>
          <Stack spacing="medium">
            <Body>{translate(`${TRANSLATION_PATH}.patientDropdown`)}</Body>
            <Grid>
              <Grid.Span columns={medCols}>
                <Dropdown
                  onChange={(patient: string) => setSelectedPatient(patient)}
                  testId="patient-dropdown"
                  value={selectedPatient}
                  placeholder={translate(`${TRANSLATION_PATH}.select`)}
                  hideLabel
                >
                  <ThemedDropdown.Option value={memberEligibility.member.person_code}>
                    {memberEligibility.member.first_name} {memberEligibility.member.last_name}
                    {memberEligibility?.member?.relationship_code === "1" && <> ({translate(`${TRANSLATION_PATH}.policyHolder`)})</>}
                    {memberEligibility?.member?.relationship_code !== "1" && <> ({translate(`${TRANSLATION_PATH}.dependent`)})</>}
                  </ThemedDropdown.Option>
                  {minorDependents?.map((dependent) => {
                    return (
                      <ThemedDropdown.Option value={dependent.member.person_code} key={dependent.member.person_code}>
                        {dependent.member.first_name} {dependent.member.last_name} ({translate(`${TRANSLATION_PATH}.dependent`)})
                      </ThemedDropdown.Option>
                    );
                  })}
                </Dropdown>
              </Grid.Span>
            </Grid>
          </Stack>
          {selectedClaimType === "pharmacy" && (
            <Stack spacing="medium">
              <Body>{translate(`${TRANSLATION_PATH}.claimInformationDropdown`)}</Body>
              <Grid>
                <Grid.Span columns={medCols}>
                  <Dropdown
                    placeholder={translate(`${TRANSLATION_PATH}.select`)}
                    onChange={(formType: string) => setSelectedFormType(formType as DirectMemberReimbursementFormFillType)}
                    testId="patient-dropdown"
                    value={selectedFormType}
                    hideLabel
                  >
                    {DMR_FORM_FILL_TYPES.map((claimInfoOption) => {
                      return (
                        <Dropdown.Option value={claimInfoOption} key={claimInfoOption}>
                          {translate(`${TRANSLATION_PATH}.${claimInfoOption}FormFillType`)}
                        </Dropdown.Option>
                      );
                    })}
                  </Dropdown>
                </Grid.Span>
              </Grid>
            </Stack>
          )}
          {isPharmacyFormFillSubmission && (
            <Stack spacing="medium">
              <Heading>{translate(`${TRANSLATION_PATH}.claimDetails`)}</Heading>
              <Grid>
                <Grid.Span columns={largeCols}>
                  <DirectMemberReimbursementPatientDetailsCard patient={patientToDisplay} isClaimPatient={true} />
                </Grid.Span>
                {dependents?.results.length > 0 && patientToDisplay?.relationship_code !== "1" && (
                  <Grid.Span columns={largeCols}>
                    <DirectMemberReimbursementPatientDetailsCard patient={memberEligibility?.member} />
                  </Grid.Span>
                )}
              </Grid>
            </Stack>
          )}
          {isMedicalUploadSubmission && selectedPatient && (
            <Grid>
              <Grid.Span columns={largeCols}>
                <DirectMemberReimbursementPatientDetailsCard patient={patientToDisplay} isClaimPatient={true} />
              </Grid.Span>
              {dependents?.results.length > 0 && patientToDisplay?.relationship_code !== "1" && (
                <Grid.Span columns={largeCols}>
                  <DirectMemberReimbursementPatientDetailsCard patient={memberEligibility?.member} />
                </Grid.Span>
              )}
            </Grid>
          )}
          {isPharmacyFormFillSubmission && (
            <DirectMemberReimbursementClaimDetailsForm onSubmitCallbackHandler={onFillPharmacyDetailsFormSubmit} />
          )}
          {isMedicalUploadSubmission && <MedicalDmrInfoSection />}
          {(isPharmacyUploadSubmission || isMedicalUploadSubmission) && (
            <DirectMemberReimbursementUploadClaimForm claimType={selectedClaimType} onSubmitCallbackHandler={onUploadClaimFormSubmit} />
          )}
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
